import { Moment } from 'moment'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import LEBookingTag from '../../../Tripkit/Common/LEBookingTag'

import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { DAY_MONTH_NAME } from 'constants/dateFormats'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import BookedLabel from 'tripPlanner/components/ItemCard/Common/BookedLabel'
import GoogleCardImage from 'tripPlanner/components/ItemCard/Common/GoogleCardImage'
import { ItemMetadata } from 'tripPlanner/models'
import { Place } from 'tripPlanner/types/common'
import { TripItem } from 'tripPlanner/types/tripItem'
import {
  formatShortDateAndTimeRange,
  formatShortDateRange,
  typeConstToTitle,
} from 'tripPlanner/utils'

export const ConstrainedMapView = styled.div`
  max-width: 260px;
  box-shadow: 0 2px 7px 1px rgb(0 0 0 / 30%);
`

export const Container = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  box-shadow: ${({ theme }) => theme.shadow.flat.small};
  border: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.M};
  overflow: hidden;
  height: max-content;
  width: ${rem(260)};
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  gap: ${rem(8)};
  padding: ${rem(16)};

  & > span {
    width: min-content;
  }

  & > :last-child {
    margin-top: ${rem(8)};
  }
`

const SubContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${rem(4)};
`

export const Image = styled.img`
  position: absolute;
  object-fit: cover;
  object-position: center;
  height: 100%;
  width: 100%;
`

export const ImageContainer = styled.div`
  position: relative;
  align-self: stretch;
  width: 100%;
  height: 160px;
`

export const ImageOverlayContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  left: ${rem(12)};
  right: ${rem(12)};
`

export const TimePlace = styled.div`
  display: flex;
  justify-content: space-between;

  & > section:nth-child(odd) {
    display: flex;
    width: max-content;
    flex-direction: column;
    gap: ${rem(4)};
  }

  & > section:nth-child(even) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${rem(4)};
  }
`

interface BaseProps {
  name: string
  type: TripItem['type']
  typeLabel?: string
  isBooked: boolean
  linksToOffer?: boolean
  region?: string
  duration?: string
  onClick?: () => void
  canEdit: boolean
  to?: string
}

const TypeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

export const renderImage = (place: Place) => {
  if (place.type === 'GEO' && place.id) {
    return (
      <ImageContainer>
        <GoogleCardImage placeId={place.id} title={place.name} />
      </ImageContainer>
    )
  }

  if (place.photoUrl) {
    return (
      <ImageContainer>
        <Image src={place.photoUrl} />
      </ImageContainer>
    )
  }

  return null
}

interface DatesProps extends BaseProps {
  startDate?: Moment
  endDate?: Moment
  startDay?: number
  endDay?: number
}

export function DatesVariant({
  name,
  isBooked,
  region,
  startDate,
  endDate,
  duration,
  type,
  typeLabel,
  onClick,
  to,
  linksToOffer = false,
  canEdit,
  startDay,
  endDay,
}: DatesProps) {
  const Icon = ItemMetadata[type].icon

  return (
    <Content>
      <Group direction="horizontal" horizontalAlign="space-between">
        <TypeLabel>
          <Icon />
          <BodyText variant="small">
            {typeLabel ?? typeConstToTitle(type)}
          </BodyText>
        </TypeLabel>
        {isBooked && <BookedLabel />}
      </Group>
      <SubContent>
        {region && <BodyText variant="small">{region}</BodyText>}
        <Heading variant="heading6" lineClamp={3}>
          {name}
        </Heading>
        <BodyText variant="small">
          {formatShortDateRange({ startDate, endDate, startDay, endDay })}
          {duration && <> &bull; {duration}</>}
        </BodyText>
      </SubContent>
      {isBooked && linksToOffer && <LEBookingTag />}
      <TextButton
        kind="primary"
        to={to}
        onClick={onClick}
        target={!canEdit ? '_blank' : undefined}
      >
        {linksToOffer ? 'View Offer' : 'View Details'}
      </TextButton>
    </Content>
  )
}

interface DateTimesProps {
  name: string
  type: TripItem['type']
  typeLabel?: string
  isBooked: boolean
  hasOffer?: boolean
  onClick: () => void
  to?: string
  startDate?: Moment
  startTime?: Moment
  endDate?: Moment
  endTime?: Moment
  startPlace: Place
  endPlace: Place
  startDay?: number
  endDay?: number
}

export function DateTimesVariant({
  name,
  isBooked,
  startPlace,
  startDate,
  startTime,
  endDate,
  endTime,
  type,
  typeLabel,
  onClick,
  startDay,
  endDay,
  hasOffer = false,
}: DateTimesProps) {
  const Icon = ItemMetadata[type].icon

  const timeFormat = useRegionTimeFormat()

  const displayDates = useMemo(
    () =>
      startDay
        ? formatShortDateRange({ startDay, endDay })
        : formatShortDateAndTimeRange({
            startDate,
            startTime,
            endDate,
            endTime,
            timeFormat,
          }),
    [endDate, endDay, endTime, startDate, startDay, startTime, timeFormat],
  )

  return (
    <Content>
      <Group direction="horizontal" horizontalAlign="space-between">
        <TypeLabel>
          <Icon />
          <BodyText variant="small">
            {typeLabel ?? typeConstToTitle(type)}
          </BodyText>
        </TypeLabel>
        {isBooked && <BookedLabel />}
      </Group>
      <SubContent>
        <BodyText as="p" variant="small" lineClamp={2}>
          {startPlace.region}
        </BodyText>
        <Heading variant="heading6" lineClamp={3}>
          {name}
        </Heading>
        <BodyText as="p" variant="small">
          {displayDates}
        </BodyText>
      </SubContent>
      {isBooked && hasOffer && <LEBookingTag />}
      <TextButton kind="primary" onClick={onClick}>
        {hasOffer ? 'View Offer' : 'View Details'}
      </TextButton>
    </Content>
  )
}

interface SingleDateProps extends BaseProps {
  date?: Moment
  time?: Moment
  day?: number
}

export function SingleDateVariant({
  name,
  isBooked,
  region,
  date,
  day,
  time,
  duration,
  type,
  typeLabel,
  onClick,
  to,
  linksToOffer = false,
  canEdit,
}: SingleDateProps) {
  const Icon = ItemMetadata[type].icon

  const timeFormat = useRegionTimeFormat()

  return (
    <Content>
      <Group direction="horizontal" horizontalAlign="space-between">
        <TypeLabel>
          <Icon />
          <BodyText variant="small">
            {typeLabel ?? typeConstToTitle(type)}
          </BodyText>
        </TypeLabel>
        {isBooked && <BookedLabel />}
      </Group>
      <SubContent>
        {region && <BodyText variant="small">{region}</BodyText>}
        <Heading variant="heading6" lineClamp={3}>
          {name}
        </Heading>
        <BodyText variant="small">
          {day ? `Day ${day}` : date?.format(DAY_MONTH_NAME)}
          {time && <> &bull; {time.format(timeFormat)}</>}
          {duration && <BodyText variant="small">{duration}</BodyText>}
        </BodyText>
      </SubContent>
      {isBooked && linksToOffer && <LEBookingTag />}
      <TextButton
        kind="primary"
        to={to}
        onClick={onClick}
        target={!canEdit ? '_blank' : undefined}
      >
        {linksToOffer ? 'View Offer' : 'View Details'}
      </TextButton>
    </Content>
  )
}
